import { useState, useEffect } from "react";
import NitroService from "../utils/nitro.service";
import { useNitroStore } from "../store/Store";
import {
  IGetQuotesParams,
  IOriginExtension,
  IQuotesByDestinationOld,
} from "../types/ICompAnalysisQMD";
import { convertCompAnalysisQMDOld } from "../utils/convertCompAnalysisQMDOld";

export interface IQuotesResponse {
  destinationQuotes: IQuotesByDestinationOld[];
  loading: boolean;
  error: Error | null;
}

export const useQuotesForDestinationsOld = (
  quoteParams: IGetQuotesParams,
  refetchTrigger?: number,
  disabled?: boolean,
): IQuotesResponse => {
  const [featureFlags] = useNitroStore((state) => [state.featureFlags]);
  const { manageRailRate } = featureFlags;
  const [quotesResponse, setQuotesResponse] = useState<IQuotesResponse>({
    destinationQuotes: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    // Skip fetch if disabled, or both destination and party are null (no search to perform)
    if (disabled || (!quoteParams.id && !quoteParams.party)) {
      setQuotesResponse({
        destinationQuotes: [],
        loading: false,
        error: null,
      });
      return;
    }

    setQuotesResponse((prevData) => ({
      ...prevData,
      loading: true,
      error: null,
    }));

    const fetchData = async () => {
      try {
        const quotes = await NitroService.getQuotesByDestinationOld(
          quoteParams,
          0,
          0,
        );

        // Fetch and append pin types for origins
        // TODO: we also get details for favorite origins with rail turned on. do we need to do both sections or are we unecessarily duplicating calls?. now put this logic in convert function
        const originIds = new Set<string>();
        quotes.forEach((destination) => {
          destination.quotes.forEach((quote) => {
            if (quote.origin?.id) {
              originIds.add(quote.origin.id);
            }
          });
        });

        const locationDetailsMap = new Map<string, any>();
        if (originIds.size > 0) {
          try {
            const detailsArray = await NitroService.getMultiLocationDetails({
              ids: Array.from(originIds),
            });
            if (detailsArray) {
              detailsArray.forEach((details) => {
                if (details?.id) {
                  locationDetailsMap.set(details.id, details);
                }
              });
            }
          } catch (error) {
            console.error("Error fetching multi-location details:", error);
          }
        }

        quotes.forEach((destination) => {
          destination.quotes.forEach((quote) => {
            if (quote.origin?.id && locationDetailsMap.has(quote.origin.id)) {
              const locationDetails = locationDetailsMap.get(quote.origin.id);
              if (locationDetails) {
                const origin = quote.origin as IOriginExtension;
                // eslint-disable-next-line no-param-reassign
                origin.pinType = locationDetails.pinType;
                // eslint-disable-next-line no-param-reassign
                origin.city = locationDetails.city;
                // eslint-disable-next-line no-param-reassign
                origin.details = locationDetails;
              }
            }
          });
        });

        const quotesResult = await convertCompAnalysisQMDOld(quotes);

        setQuotesResponse({
          // only allow rail quotes through when the manageRailRate flag is on
          destinationQuotes: manageRailRate
            ? quotesResult
            : quotesResult.map((entry) => ({
                ...entry,
                quotes: entry.quotes.filter(
                  (quote) => quote.mode?.toUpperCase() === "TRUCK",
                ),
              })),
          loading: false,
          error: null,
        });
      } catch (err) {
        setQuotesResponse((d) => ({
          ...d,
          truckDlvdOption: null,
          railDlvdOption: null,
          loading: false,
          error: err as Error,
        }));
      }
    };

    const timer = setTimeout(fetchData, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quoteParams.id,
    quoteParams.product,
    quoteParams.party,
    quoteParams.marketer,
    quoteParams.shipDate,
    quoteParams.currency,
    quoteParams.uom,
    refetchTrigger,
    disabled,
  ]);

  return quotesResponse;
};
